import { useState } from 'react';
import { Lightbulb, X, ChevronRight, Keyboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const tips = [
  {
    title: 'Start with a hook',
    content: 'Open with a compelling question, surprising fact, or vivid scene to draw readers in immediately.',
  },
  {
    title: 'Use short paragraphs',
    content: 'Break up long blocks of text. Online readers scan, so shorter paragraphs improve readability.',
  },
  {
    title: 'Add subheadings',
    content: 'Use ## for sections to help readers navigate and understand your article structure.',
  },
  {
    title: 'Include examples',
    content: 'Abstract ideas become memorable when illustrated with concrete examples or stories.',
  },
  {
    title: 'End with purpose',
    content: 'Close with a call to action, thought-provoking question, or memorable takeaway.',
  },
];

const shortcuts = [
  { keys: ['⌘', 'S'], action: 'Save draft' },
  { keys: ['⌘', 'Enter'], action: 'Publish article' },
  { keys: ['⌘', 'B'], action: 'Bold text' },
  { keys: ['⌘', 'I'], action: 'Italic text' },
  { keys: ['⌘', 'K'], action: 'Insert link' },
];

interface WritingTipsProps {
  className?: string;
}

export function WritingTips({ className }: WritingTipsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentTip, setCurrentTip] = useState(0);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const nextTip = () => {
    setCurrentTip((prev) => (prev + 1) % tips.length);
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className={cn(
          'flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors',
          className
        )}
      >
        <Lightbulb className="w-4 h-4" />
        <span className="hidden sm:inline">Writing tips</span>
      </button>
    );
  }

  return (
    <div className={cn(
      'fixed bottom-4 right-4 w-80 bg-card border border-border rounded-xl shadow-lg animate-slide-up',
      className
    )}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShortcuts(false)}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 rounded-md text-sm transition-colors',
                !showShortcuts ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Lightbulb className="w-4 h-4" />
              Tips
            </button>
            <button
              onClick={() => setShowShortcuts(true)}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 rounded-md text-sm transition-colors',
                showShortcuts ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Keyboard className="w-4 h-4" />
              Shortcuts
            </button>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsExpanded(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {showShortcuts ? (
          <div className="space-y-2">
            {shortcuts.map((shortcut, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{shortcut.action}</span>
                <div className="flex items-center gap-1">
                  {shortcut.keys.map((key, j) => (
                    <kbd
                      key={j}
                      className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono"
                    >
                      {key}
                    </kbd>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <h4 className="font-medium text-sm mb-1">{tips[currentTip].title}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {tips[currentTip].content}
              </p>
            </div>
            <button
              onClick={nextTip}
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Next tip
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
